import * as React from "react";
import { toast } from "sonner";
import { HelpCircle, Loader2, Phone } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const MAX_MESSAGE_LEN = 4000;

/**
 * Support request dialog — opens from the "Need help?" link in the
 * sidebar. Admins + dispatchers only.
 *
 * Oct 2026 revamp — bigger, friendlier, adds an "I'd rather you called
 * me" shortcut so Steve can skip typing and just dial. Diagnostics
 * (role, tenant, page, viewport, user agent, app version) are still
 * auto-collected.
 */
export function SupportDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [message, setMessage] = React.useState("");
  const [preferCallback, setPreferCallback] = React.useState(false);
  const [callbackName, setCallbackName] = React.useState("");
  const [callbackPhone, setCallbackPhone] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [ticketRef, setTicketRef] = React.useState(null);
  const [appVersion, setAppVersion] = React.useState("");

  // Pull current app build version on first open for diagnostics.
  React.useEffect(() => {
    if (!open) return;
    api
      .get("/version")
      .then((r) => setAppVersion(r?.data?.version || ""))
      .catch(() => {});
  }, [open]);

  // Pre-fill the callback name + number from the user's profile if we
  // have them (saves a few taps on mobile).
  React.useEffect(() => {
    if (open) {
      if (!callbackName && user?.name) setCallbackName(user.name);
      if (!callbackPhone && user?.phone) setCallbackPhone(user.phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetAndClose = () => {
    setMessage("");
    setPreferCallback(false);
    setCallbackName(user?.name || "");
    setCallbackPhone(user?.phone || "");
    setTicketRef(null);
    onOpenChange?.(false);
  };

  const remaining = MAX_MESSAGE_LEN - message.length;
  const nearLimit = remaining < 200;
  const messageTooShort = message.trim().length < 5;
  const callbackNeedsName =
    preferCallback && callbackName.trim().length < 2;
  const callbackNeedsNumber =
    preferCallback && callbackPhone.trim().length < 6;
  const sendDisabled =
    sending || messageTooShort || callbackNeedsName || callbackNeedsNumber;

  const handleSend = async () => {
    if (messageTooShort) {
      toast.error("Please describe your issue in a few words");
      return;
    }
    if (callbackNeedsName) {
      toast.error("Add the name of the person we should ask for");
      return;
    }
    if (callbackNeedsNumber) {
      toast.error("Add a phone number for the callback");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/support/ticket", {
        message: message.trim(),
        prefer_callback: preferCallback,
        callback_name: preferCallback ? callbackName.trim() : null,
        callback_phone: preferCallback ? callbackPhone.trim() : null,
        page: typeof window !== "undefined" ? window.location.pathname : null,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : null,
        app_version: appVersion || null,
        viewport:
          typeof window !== "undefined"
            ? `${window.innerWidth}x${window.innerHeight}`
            : null,
      });
      const ref = r?.data?.ticket_ref;
      setTicketRef(ref || "sent");
      toast.success("Support request sent — we'll get back to you");
    } catch (e) {
      const msg =
        e?.response?.data?.detail || "Could not send your request. Try again?";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[94vw] sm:w-full"
        data-testid="support-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <HelpCircle className="h-6 w-6 text-blue-600" />
            Need help? We're here.
          </DialogTitle>
          <DialogDescription className="pt-1">
            Tell us what's going wrong — be as specific as you can. If you
            would prefer a phone call, tick the "Call me" box below and
            we'll give you a call back as soon as we can. Your role,
            current page, app version, browser and screen size are
            attached automatically so we have a snapshot of your setup.
          </DialogDescription>
        </DialogHeader>

        {ticketRef ? (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-100"
            data-testid="support-success-panel"
          >
            <div className="font-semibold text-base">Your request is on its way.</div>
            <div className="mt-2 text-sm">
              Reference:{" "}
              <span
                className="font-mono text-emerald-800 dark:text-emerald-200 font-bold"
                data-testid="support-ticket-ref"
              >
                {ticketRef}
              </span>
            </div>
            <div className="mt-3 text-xs text-emerald-800/80 dark:text-emerald-200/80">
              Quote this reference if you need to follow up. If you asked
              for a phone call, we'll ring the number you gave us as soon
              as possible — usually within business hours (NZ time).
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="support-message"
                className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5"
              >
                What's going wrong?
              </label>
              <Textarea
                id="support-message"
                placeholder={
                  "e.g. The AA job for BPK700 showed up in the queue " +
                  "then disappeared within 20 seconds. I didn't cancel it."
                }
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LEN))}
                rows={9}
                className="resize-y min-h-[200px] text-base"
                autoFocus
                maxLength={MAX_MESSAGE_LEN}
                data-testid="support-message-input"
              />
              <div className="mt-1.5 flex items-center justify-between text-xs">
                <span className="text-slate-500 dark:text-slate-400">
                  Minimum 5 characters.
                </span>
                <span
                  className={
                    nearLimit
                      ? "font-mono font-semibold text-amber-600"
                      : "font-mono text-slate-400 dark:text-slate-500"
                  }
                  data-testid="support-char-counter"
                >
                  {message.length.toLocaleString()} / {MAX_MESSAGE_LEN.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-3.5 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferCallback}
                  onChange={(e) => setPreferCallback(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                  data-testid="support-prefer-callback"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 font-semibold text-sm text-slate-800 dark:text-slate-100">
                    <Phone className="h-3.5 w-3.5" />
                    I'd rather have a quick phone call
                  </div>
                </div>
              </label>
              {preferCallback && (
                <div className="pl-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="support-callback-name"
                      className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1"
                    >
                      Name
                    </label>
                    <Input
                      id="support-callback-name"
                      type="text"
                      placeholder="e.g. Steve"
                      value={callbackName}
                      onChange={(e) => setCallbackName(e.target.value)}
                      maxLength={80}
                      data-testid="support-callback-name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="support-callback-phone"
                      className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1"
                    >
                      Phone number
                    </label>
                    <Input
                      id="support-callback-phone"
                      type="tel"
                      placeholder="e.g. 027 123 4567"
                      value={callbackPhone}
                      onChange={(e) => setCallbackPhone(e.target.value)}
                      data-testid="support-callback-phone"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {ticketRef ? (
            <Button onClick={resetAndClose} data-testid="support-close-btn">
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange?.(false)}
                disabled={sending}
                data-testid="support-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={sendDisabled}
                data-testid="support-send-btn"
              >
                {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {preferCallback ? "Request callback" : "Send"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SupportDialog;
